import { Component, OnInit, Output, ViewChild, EventEmitter, Input } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { TabViewModule } from "primeng/tabview";
import { AppSettings } from "../../../../../shared/app.settings";
import { MiFormComponent } from "../../../../../shared/components/mi-form/mi-form.component";
import type { Country } from "../../../../../shared/models/country";
import type { Language } from "../../../../../shared/models/language";
import { CommonBindingDataService } from "../../../../../shared/services/common-binding-data.service";
import { EntityService } from "../../../../../shared/services/entity.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { EntityResponse, RelatedDatum } from "app/modules/shared/models/entityResponse.models";
import { AttributeData, Relation } from "app/modules/shared/models/attribute.models";
import { MetaDataService } from "app/modules/shared/services/meta-data.service";
import { RestApiService } from "app/modules/shared/services/rest-api.service";

@Component({
  selector: 'app-add-protocol',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule
  ],
  templateUrl: './add-protocol.component.html',
  styleUrl: './add-protocol.component.scss'
})
export class AddProtocolComponent implements OnInit {

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  @Output() closePopup = new EventEmitter<void>();
  @Input() protocolId: string;
  @Input() sponsorRelatedData: Record<string, unknown>;
  activeIndex: number = 0;
  data: AttributeData;
  protocolAttributeData: AttributeData;
  routePath: MenuItem[] = [];
  attributeLabels: Record<string, string> = {};
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: Array<Record<string, unknown>> = [];
  protocolAttributeLength: number;
  protocolIdForDocument: string;
  sponsorRelation: Relation;
  attributeValues: Record<string, unknown> = {};
  nextBtnLabel: string;
  previousBtnLabel: string;
  label: string;
  initialLabel: string = this.cs.getLabel("lbl_details");
  saveProtocolBtnLabel: string;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode: string;
  loadDialogView = false;
  protocolHeader: string;
  successMessage: string;
  sponsorStatusAttributeId: number;
  private documentDetailsMap = new Map<string, any>();

  constructor(
    private protocolService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService
  ) { }

  ngOnInit() {
    this.retrieveProtocolId();
    this.initializeLabels();
    this.initializeProtocolService();
    this.loadProtocolData();
    this.activeIndex = 0;
  }

  private initializeLabels() {
    if (this.protocolId) {
      this.protocolHeader = this.cs.getLabel("protocol.lbl_update_protocol");
      this.nextBtnLabel = this.cs.getLabel("protocol.lbl_update_protocol");
      this.successMessage = this.cs.getLabel('protocol.lbl_protocol_updated_successfully')
    } else {
      this.protocolHeader = this.cs.getLabel("protocol.lbl_add_new_protocol");
      this.nextBtnLabel = this.cs.getLabel("protocol.lbl_save_protocol");
      this.successMessage = this.cs.getLabel('protocol.lbl_protocol_added_successfully')
    }
    this.previousBtnLabel = this.cs.getLabel("lbl_cancel");
  }

  private retrieveProtocolId() {
  }

  private initializeProtocolService() {
    this.moduleName = AppSettings.ENTITY_TYPE.PROTOCOL;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.protocolService.setRelatedData([]);
    this.documentDetailsMap.clear();
  }

  private loadProtocolData() {
    if (this.protocolId) {
      this.protocolService
        .getEntity(this.protocolId, AppSettings.ENTITY_CODE.PROTOCOL, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe((res: EntityResponse) => {
          if (res) {
            this.processProtocolResponse(res);
            this.isNew = false;
          }
        });
    } else {
      this.getAttributes();
      this.attributeValues.sponsor_entity_id = this.sponsorRelatedData.sponsor[0].entityId;
      this.protocolService.setRelatedData(this.sponsorRelatedData);
    }
  }

  private toFileIdArray(value: any): string[] {
    if (!value) return [];
    const arr: any[] = Array.isArray(value) ? [].concat(...value) : [String(value)];
    return arr.filter(Boolean).map(String);
  }

  private processProtocolResponse(data: EntityResponse) {
    this.attributeValues = this.cs.getOrgAttributeValues(data) as unknown as Record<string, unknown>;
    this.normalizeStartEndDate();
    this.processRelatedData(data.relatedData as unknown as Array<RelatedDatum>);
    const fileIds = this.toFileIdArray(this.attributeValues.ctn_file_upload);
    if (fileIds.length > 0) {
      this.restApiService.post(
        'get protocol document details for edit',
        `${this.configService.getLoggedInTenantCode()}/api/v1/files/details`,
        { forTenantCode: this.configService.getForTenantCode(), uploadType: AppSettings.DOCUMENTS_TYPE.DOCUMENT, fileIds }
      ).subscribe({
        next: (fileDetails: any) => {
          if (Array.isArray(fileDetails)) {
            fileDetails.forEach(f => {
              if (f.fileId) this.documentDetailsMap.set(f.fileId, f);
              if (f.resourceId) this.documentDetailsMap.set(f.resourceId, f);
            });
          }
          this.getAttributes();
        },
        error: () => this.getAttributes()
      });
    } else {
      this.getAttributes();
    }
  }

  private normalizeStartEndDate() {
    const startEndDateCodes = ['start_date', 'end_date'];
    for (const dateCode of startEndDateCodes) {
      if (this.attributeValues[dateCode]) {
        const dateValue = this.metaDataService.setDateInTimezone(this.attributeValues[dateCode] as number);
        this.attributeValues[dateCode] = new Date(dateValue as string | number);
      }
    }
  }

  private processRelatedData(relatedData: Array<RelatedDatum>) {
    if (relatedData.length > 0) {
      const organizedData: { [key: string]: Array<Record<string, unknown>> } = {};
      relatedData.forEach(item => {
        if (item.actionStatus === "DELETED") {
          return;
        }
        const entityCode = item.entityCode as string;
        if (!organizedData[entityCode]) {
          organizedData[entityCode] = [];
        }
        const entityData = this.cs.getOrgAttributeValues(item) as unknown as Record<string, unknown>;
        entityData.entityId = item.entityId;
        entityData.entity_name = entityData.sponsor_name;
        organizedData[entityCode].push(entityData);
      });
      this.protocolService.setRelatedData(organizedData);
    }
  }

  getAttributes() {
    const entityType = AppSettings.ENTITY_TYPE.PROTOCOL;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.protocolService.getAttributeDefinition(AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW)
      .subscribe((filterResponse: unknown) => {
        if (filterResponse) {
          const sponsorStatus = this.cs.getOrganizedAttribute(filterResponse)?.tabs[0]?.groups[0]?.fields?.find(
            (element: { attributeCode: string }) => element.attributeCode === 'sponsor_status')?.attributeId;
          if (sponsorStatus) {
            this.sponsorStatusAttributeId = sponsorStatus;
           this.protocolService.getAttributeDefinition(entityType, viewCode).subscribe(res => {
            if (!res) return;
            this.setProtocolView(res);
          });
          }
        }
    });
  }

  setProtocolView(res: AttributeData) {
    this.loadDialogView = true;
    this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
    this.protocolAttributeData = this.cs.getOrganizedAttribute(res) as unknown as AttributeData;
    this.protocolAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.protocolAttributeData?.tabs);
    this.protocolAttributeLength = this.protocolAttributeData.tabs.length;
    this.sponsorRelation = _.find(res?.relations, { otherEntityCode: 'sponsor', relation: "oneToOne" });
    this.protocolAttributeData.tabs = this.protocolAttributeData.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.filter((field: any) => {
          if (field.attributeCode === 'ctn_file_upload') {
            const fileIds = this.toFileIdArray(this.attributeValues.ctn_file_upload);
            field.documentURLs = fileIds.map(id => this.documentDetailsMap.get(id)?.signedUrl).filter(Boolean);
            field.documentNames = fileIds.map(id => this.documentDetailsMap.get(id)?.originalFileName || 'Document');
          }
          if (field.attributeCode === 'sponsor_entity_id') {
            field.moduleName = AppSettings.ENTITY_CODE.SPONSOR;
            field.disabled = true;
            field.filter = [{
              attributeId: this.sponsorStatusAttributeId,
              attributeValue: "Active"
            }];
            return true;
          }
          return true;
        })
      }))
    }));
  }

  onSaveSponsor(event: Record<string, unknown>) {
    const protocolData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL,
      countryCode: this.country[0].countryCode,
      languageCode: this.language[0].langCode,
      data: [],
      relationshipData: []
    };

    const vData = event;
    const dateIds = ["start_date", "end_date"];
    for (const [key, value] of Object.entries(vData)) {
      let attributeValue = value as unknown;
      if (dateIds.includes(key)) {
        const dateValue = this.metaDataService.getTimeZoneFormattedDate(value as Date);
        if(key === 'end_date') {
          attributeValue = new Date(new Date(dateValue as string).setHours(23, 59, 59, 999)).getTime();
        } else {
          attributeValue = new Date(dateValue as string).getTime();
        }
        protocolData.data.push({
          attributeCode: key,
          attributeValue
        });
      } else if (key === 'sponsor_entity_id') {
        protocolData?.relationshipData?.push({
          entityRelationshipConfigId: this.sponsorRelation.entityRelationshipConfigId,
          otherEntityId: value
        });
        protocolData.data.push({
          attributeCode: key,
          attributeValue,
        });
      } else if (key === 'ctn_file_upload') {
        const ids = this.toFileIdArray(attributeValue);
        if (ids.length > 0) {
          protocolData.data.push({ attributeCode: key, attributeValue: ids });
        }
      } else if (attributeValue || (attributeValue as string)?.length) {
        protocolData.data.push({
          attributeCode: key,
          attributeValue
        });
      }
    }

    const entitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };
    if (!this.protocolId) {
      this.protocolService
        .createEntities(this.moduleName, entitiesData)
        .subscribe((res: EntityResponse) => {
          this.protocolId = res.entityId;
          this.saveProtocolData(protocolData, true);
        });

    } else {
      this.saveProtocolData(protocolData, false);
    }
  }

  saveProtocolData(protocolData: Record<string, unknown>, isNew: boolean) {
    protocolData.data = this.cs.mapAttributeIds(protocolData.data, this.protocolAttributeData.tabs)
    this.protocolService
      .saveAttributeData(protocolData.entityCode as string, this.protocolId, protocolData).subscribe({
        next: () => {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.successMessage });
          this.closeDialog(true);
        },
        error: () => {
          this.cs.onDisplayMessage(this.data, 'protocol', isNew ? AppSettings.MSG_ACTION.ADD : AppSettings.MSG_ACTION.UPDATE, 'error');
        }
      });
  }

  onPreviousClick() {
    this.closeDialog(false);
  }

  closeDialog(data) {
    this.closePopup.emit(data);
  }
}
